/**
 * ========================================
 * NOTIFICATION BELL
 * ========================================
 * Header bell icon + dropdown feed, opened from anywhere in the app.
 * Polls the unread count periodically; fetches the list on open.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationService } from '@/services/api/notification.service';
import type { AppNotification } from '@/types/notification';
import { NOTIFICATION_PRIORITY_DOT } from '@/types/notification';

const POLL_INTERVAL_MS = 30000;

const timeAgo = (iso?: string | null): string => {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

interface NotificationBellProps {
  /** Extra classes for the trigger button, e.g. theme-aware hover state. */
  buttonClassName?: string;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({ buttonClassName = 'hover:bg-black/5' }) => {
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const refreshUnreadCount = useCallback(async () => {
    try {
      const count = await notificationService.unreadCount();
      setUnreadCount(count);
    } catch {
      // Silent — the bell just won't show a badge if this fails.
    }
  }, []);

  useEffect(() => {
    refreshUnreadCount();
    const interval = setInterval(refreshUnreadCount, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [refreshUnreadCount]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const result = await notificationService.list({ page: 1, page_size: 20 });
        if (!cancelled) setNotifications(result.items);
      } catch {
        // Silent — dropdown just shows empty state.
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = async (notification: AppNotification) => {
    if (!notification.is_viewed) {
      setNotifications((prev) => prev.map((n) => (n.id === notification.id ? { ...n, is_viewed: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
      notificationService.markRead(notification.id).catch(() => {});
    }
    setIsOpen(false);
    if (notification.action_url) navigate(notification.action_url);
  };

  const handleMarkAllRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, is_viewed: true })));
    setUnreadCount(0);
    try {
      await notificationService.markAllRead();
    } catch {
      refreshUnreadCount();
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        onClick={() => setIsOpen((v) => !v)}
        className={`relative p-2 rounded-lg transition-colors text-current ${buttonClassName}`}
        title="Notifications"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 max-w-[90vw] bg-white border border-gray-200 rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-bold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button onClick={handleMarkAllRead} className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">
            {isLoading ? (
              <p className="text-sm text-gray-400 text-center py-8">Loading…</p>
            ) : notifications.length === 0 ? (
              <div className="text-center py-10 px-4">
                <div className="text-3xl mb-2">🔔</div>
                <p className="text-sm text-gray-400">No notifications yet</p>
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors hover:bg-gray-50 ${
                    !n.is_viewed ? 'bg-indigo-50/60' : ''
                  }`}
                >
                  <span className="text-xl leading-none flex-shrink-0 mt-0.5">{n.icon || '🔔'}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className={`text-sm break-words ${!n.is_viewed ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                        {n.title}
                      </p>
                      {!n.is_viewed && <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${NOTIFICATION_PRIORITY_DOT[n.priority]}`} />}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 break-words line-clamp-2">{n.message}</p>
                    <p className="text-[11px] text-gray-400 mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
